"use client";

import { Info, LinkIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import * as React from "react";
import { AccessRules, Page, SummaryDetail } from "types";

import CopyLink from "@/components/copy-link";
import { ExaminePlusLink, OverlayCta } from "@/components/cta";
import EndsTonight from "@/components/ends-tonight";
import { LockBadge } from "@/components/lock";
import PaidPill from "@/components/paid-pill";
import ResearchCard from "@/components/research-card";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { buttonVariants } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAccess } from "@/lib/hooks/use-access";
import { useExpandCollapse } from "@/lib/hooks/use-expand-collapse";
import { cn } from "@/lib/utils";

import SaleWrapper from "./sale-wrapper";

interface ResearchFeedProps {
	summaries: SummaryDetail[];
	page: Page;
	accessRules?: AccessRules;
}

const ResearchFeed = ({ summaries, page, accessRules }: ResearchFeedProps) => {
	const cardIds = summaries?.length ? summaries.map((summary) => summary?.id) : [];
	const hasAccess = useAccess("study_summaries");

	const { setCardExpanded, isCardExpanded } = useExpandCollapse(cardIds);

	const copyUrl = `${page?.url}/#research-feed`;
	const allStudiesUrl = `/research-feed/filter/?filter=${page.type}&value=${page.slug}`;
	const { user, study_summaries: _hasAccess } = accessRules;

	if (!summaries?.length) return null;

	return (
		<Accordion type="single" id="research-feed" defaultValue="research-feed" collapsible>
			<AccordionItem value="research-feed">
				<AccordionTrigger
					as="h2"
					className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
				>
					<span className="flex w-full flex-wrap items-center justify-between gap-2">
						<span className="flex flex-wrap items-center gap-2">
							Research Feed
							<Tooltip>
								<TooltipTrigger asChild>
									<Info strokeWidth={1.5} size={16} className="mt-1.5 font-sans" />
								</TooltipTrigger>
								<TooltipContent>
									<p>
										These are summaries of the newest research on {page?.name}. <br />
										<br /> To see more summaries on this (and other) topics, click on &quot;Read all
										studies&quot;
									</p>
								</TooltipContent>
							</Tooltip>
							<span className="hidden items-center md:flex">
								{hasAccess ? (
									<PaidPill />
								) : (
									<LockBadge
										size={9}
										tooltip={<OverlayCta location={`${page?.name} RF label`} />}
										className="hidden align-top md:inline-flex"
									/>
								)}
							</span>
						</span>
						<span className="flex items-center gap-2">
							<CopyLink url={copyUrl} />
							<Link
								href={allStudiesUrl}
								className={cn(
									buttonVariants({ variant: "outline", size: "sm" }),
									"mr-1.5 font-inter text-xs sm:px-3 sm:py-1.5 lg:text-sm"
								)}
							>
								Read all studies <LinkIcon size="13" className="hidden sm:inline-block" />
							</Link>
						</span>
					</span>
				</AccordionTrigger>

				<AccordionContent>
					<SaleWrapper>
						<ExaminePlusLink
							className="mt-5 flex items-center justify-center gap-2 bg-examine-purple-100 p-4 text-gray-900"
							trackDetails={{
								trackFor: "sale",
								location: `Above ${page.name} Research Feed`,
								label: "Examine Logo <<ON SALE>> Unlock the latest research!",
							}}
						>
							<div className="relative h-5 w-20 md:h-7 md:w-32">
								<Image src="/sales/logo.svg" fill alt={"Examine"} />
							</div>
							<p className="mt-2 rounded-full bg-examine-green-500 px-2 py-1.5 font-sans text-sm font-semibold text-white">
								<EndsTonight dayBeforeCta={"SALE ENDS TOMORROW"} lastDay={"SALE ENDS TONIGHT"}>
									ON SALE
								</EndsTonight>
							</p>
							<p className="pt-2">Unlock the latest research!</p>
						</ExaminePlusLink>
					</SaleWrapper>
					<div className="relative mt-4 w-full space-y-8">
						{summaries?.map((summary: { id: string }, idx: number) => {
							let showContent = true;
							let showQuickSummary = true;
							let isSecondSummary = true;
							const isUser = !!user;

							if (!hasAccess && idx > 1) {
								showContent = false;
							}

							if (!hasAccess && ((!isUser && idx >= 1) || (isUser && idx > 1))) {
								showQuickSummary = false;
								showContent = false;
							}

							if (idx != 1) {
								isSecondSummary = false;
							}

							const rules = {
								showContent,
								showQuickSummary,
								isSecondSummary,
								showActionbar: true,
								isResearchFeed: true,
								isUser,
								hasAccess,
								setExpanded: (expanded: boolean) => setCardExpanded(summary.id, expanded),
								isExpanded: isCardExpanded(summary.id),
							};

							return <ResearchCard key={summary.id} summary={summary} rules={{ ...rules }} />;
						})}
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
};

export { ResearchFeed };
